import React, { useState, useContext, useEffect } from "react";
import StarekContext from "../../context/StarekContext";
import { gql, useQuery } from "@apollo/client";
import ProductionReport from "../../components/Reports/ProductionReport";
import EfficiencyReport from "../../components/Reports/EfficiencyReport";
import RawOnOffReport from "../../components/Reports/RawOnOffReport";
import FilterSidebar from "../../components/Reports/FilterSidebar";
import RangeSelector from "../../components/Reports/RangeSelector";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BoltIcon from "@mui/icons-material/Bolt";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

const TABS = {
  PRODUCTION: "production",
  EFFICIENCY: "efficiency",
  RAW: "raw-on-off",
};

function ReportHeader({ activeTab, onChangeTab }) {
  const baseButton =
    "tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-1.5 tw-text-xs tw-rounded-md";

  const activeClasses =
    "tw-bg-blue-50 tw-text-slate-900 tw-font-medium tw-border tw-border-blue-500";
  const inactiveClasses =
    "tw-text-slate-700 hover:tw-bg-slate-100 tw-border tw-border-transparent";

  const tabButton = (tabKey, label, Icon) => (
    <button
      type="button"
      className={`${baseButton} ${
        activeTab === tabKey ? activeClasses : inactiveClasses
      }`}
      onClick={() => onChangeTab(tabKey)}
    >
      <Icon fontSize="small" className="tw-opacity-90" />
      {label}
    </button>
  );

  const toggleProdEff = () => {
    if (activeTab === TABS.PRODUCTION) return onChangeTab(TABS.EFFICIENCY);
    if (activeTab === TABS.EFFICIENCY) return onChangeTab(TABS.PRODUCTION);
  };

  const toggleEffRaw = () => {
    if (activeTab === TABS.EFFICIENCY) return onChangeTab(TABS.RAW);
    if (activeTab === TABS.RAW) return onChangeTab(TABS.EFFICIENCY);
  };

  const ToggleButton = ({ onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="tw-inline-flex tw-items-center tw-justify-center tw-h-7 tw-w-7"
    >
      <SwapHorizIcon fontSize="small" className="tw-text-slate-500" />
    </button>
  );

  return (
    <div className="tw-flex tw-items-center tw-border-b tw-border-slate-200 tw-pb-2 tw-gap-3">
      {tabButton(TABS.PRODUCTION, "Production Report", BoltIcon)}
      <ToggleButton onClick={toggleProdEff} />
      {tabButton(TABS.EFFICIENCY, "Efficiency Report", EqualizerIcon)}
      <ToggleButton onClick={toggleEffRaw} />
      {tabButton(TABS.RAW, "Raw On-Off Report", FiberManualRecordIcon)}
    </div>
  );
}

function formatDateForSummary(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString(undefined, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildViewingSummary(activeTab, filters) {
  if (!filters || !filters.devices) return "Select filters from to view data";
  const reportName =
    activeTab === TABS.PRODUCTION
      ? "Production Report"
      : activeTab === TABS.EFFICIENCY
      ? "Efficiency Report"
      : "Raw On-Off Report";

  let devicesPart = "for all devices";

  if (filters.devices) {
    const arr = filters.devices.split(",");

    if (arr.length === 1) {
      devicesPart = `for ${arr[0]}`;
    } else if (arr.length === 2) {
      devicesPart = `for ${arr[0]}, ${arr[1]}`;
    } else if (arr.length > 2) {
      devicesPart = (
        <>
          for {arr[0]}, {arr[1]} and{" "}
          <span
            className="tw-underline tw-cursor-pointer"
            title={arr.join(", ")}
          >
            +{arr.length - 2}
          </span>
        </>
      );
    }
  }

  let durationPart = "";

  if (filters.duration?.startsWith("shift_")) {
    durationPart = `between shift on ${filters.shiftDate}`;
  } else if (filters.duration === "custom") {
    durationPart = `between ${formatDateForSummary(
      filters.customStart
    )} and ${formatDateForSummary(filters.customEnd)}`;
  } else {
    durationPart = `for last ${filters.duration
      .replace("h", " hours")
      .replace("d", " days")}`;
  }

  return (
    <>
      You are viewing {reportName} {devicesPart} {durationPart}
    </>
  );
}

const GET_DEVICES_IN_PLANT = gql`
  query GetDevicesForPlant($where: ShadowDeviceWhereInput) {
    shadowDevices(where: $where) {
      id
      name
      device
    }
  }
`;

const GET_SHIFT_LIST_FOR_PLANT = gql`
  query GetShifts($plantId: Int!) {
    shifts(where: { plant_id: { equals: $plantId } }) {
      id
      name
      description
      start_time
      end_time
      runs_on
      created_at
    }
  }
`;

export default function Reports() {
  const [activeTab, setActiveTab] = useState(TABS.RAW);
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  const [filters, setFilters] = useState({
    devices: "",
    interval: "",
    duration: "",
    shiftDate: "",
  });

  const { lastUsedWorkspace, auth, lastUsedPlant } = useContext(StarekContext);

  const { data: deviceData } = useQuery(GET_DEVICES_IN_PLANT, {
    variables: {
      where: {
        plant: {
          is: {
            id: {
              equals: lastUsedPlant?.id,
            },
          },
        },
      },
    },
    skip: !lastUsedPlant?.id,
  });

  const { data: shiftData } = useQuery(GET_SHIFT_LIST_FOR_PLANT, {
    variables: { plantId: lastUsedPlant?.id },
    skip: !lastUsedPlant?.id,
  });

  // Default View: Last Shift Completed
  useEffect(() => {
    if (!shiftData?.shifts || !deviceData?.shadowDevices) return;
    if (filters.devices) return;

    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const currentSeconds =
      now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

    const completedShifts = shiftData.shifts.filter((shift) => {
      return shift.end_time < currentSeconds;
    });

    let targetShift = null;

    if (completedShifts.length > 0) {
      completedShifts.sort((a, b) => b.end_time - a.end_time);
      targetShift = completedShifts[0];
    } else {
      if (shiftData.shifts.length > 0) {
        targetShift = shiftData.shifts[shiftData.shifts.length - 1];
      }
    }

    if (targetShift) {
      const allDeviceIds = deviceData.shadowDevices.map((d) => d.id).join(",");
      let diff = targetShift.end_time - targetShift.start_time;
      if (diff < 0) diff += 86400;

      setFilters({
        devices: allDeviceIds,
        duration: `shift_${targetShift.id}`,
        shiftDate: todayStr,
        interval: "",
        durationStart: null,
      });

      const base = new Date(todayStr);
      const startDate = new Date(base);
      const endDate = new Date(base);

      startDate.setSeconds(targetShift.start_time);
      endDate.setSeconds(targetShift.end_time);

      if (targetShift.end_time < targetShift.start_time) {
        endDate.setDate(endDate.getDate() + 1);
      }

      setFilters((prev) => ({
        ...prev,
        durationStart: startDate.toISOString(),
        durationEnd: endDate.toISOString(),
        durationSeconds: diff,
      }));
      setRange([0, diff]);
    }
  }, [shiftData, deviceData]);

  const [range, setRange] = useState([0, 0]);
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFilters({
      devices: "",
      interval: "",
      duration: "",
    });
    setRange([0, 0]);
  };

  const formatDateTime = (d) =>
    d.toLocaleString(undefined, {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatTimeOnly = (d) =>
    d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });

  function getWindowLabel(filters, range) {
    const { duration, durationStart } = filters || {};
    if (!durationStart || !Array.isArray(range) || range.length !== 2) {
      return "Window not set";
    }

    const base = new Date(durationStart);
    if (Number.isNaN(base.getTime())) return "Window not set";

    const [startOffsetSec, endOffsetSec] = range;
    const start = new Date(base.getTime() + startOffsetSec * 1000);
    const end = new Date(base.getTime() + endOffsetSec * 1000);

    const isShift = duration.startsWith("shift_");
    const isCustom = duration === "custom";
    const isByDuration = !isShift && !isCustom;

    if (isCustom || isShift) {
      return `${formatDateTime(start)} – ${formatDateTime(end)}`;
    }

    if (isByDuration) {
      return `${formatTimeOnly(start)} – ${formatTimeOnly(end)}`;
    }

    return "Window not set";
  }

  const handleApply = (payload) => {
    console.log("APPLIED FILTER PAYLOAD:", payload);
    setFilters(payload);

    const windowSize = payload.intervalSeconds || payload.durationSeconds || 0;
    const clampedWindow = Math.min(
      windowSize,
      payload.durationSeconds || windowSize || 0
    );

    setRange([0, clampedWindow]);
    setIsFilterOpen(false);
  };

  const fetchRawData = async () => {
    if (!filters.devices || !filters.durationStart || !range) {
      console.warn("Missing filters for raw data fetch");
      return null;
    }

    const { scheme, host, port } = lastUsedWorkspace.engine;
    const workspaceId = lastUsedWorkspace.name;

    // Calculate start and end time from durationStart and range
    const baseTime = new Date(filters.durationStart).getTime();
    const startTime = Math.floor((baseTime + range[0] * 1000) / 1000);
    const endTime = Math.floor((baseTime + range[1] * 1000) / 1000);

    const url = `${scheme}://${host}:${port}/api/v1/${workspaceId}/datapoints`;

    const payload = {
      devices: filters.devices.split(",").map((d) => d.trim()),
      start_time: startTime,
      end_time: endTime,
      group_by_device: true,
      group_by_signal: true,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching raw data:", error);
      throw error;
    }
  };

  return (
    <div className="tw-relative tw-flex tw-h-full tw-min-h-[calc(100vh-80px)] tw-bg-slate-50">
      {!isFilterOpen && (
        <button
          type="button"
          onClick={() => setIsFilterOpen(true)}
          className="tw-left-0 tw-top-16 tw-h-10 tw-z-20 tw-inline-flex tw-items-start tw-justify-center tw-rounded-md tw-border tw-border-slate-300 tw-bg-white tw-p-2 hover:tw-bg-slate-100"
          title="Filters"
        >
          <ChevronRightIcon />
        </button>
      )}

      {isFilterOpen && (
        <div className="tw-w-1/4 tw-max-w-[25%] tw-border-r tw-border-slate-200 tw-bg-white tw-p-4 tw-flex tw-flex-col">
          <FilterSidebar
            filters={filters}
            onChange={handleFilterChange}
            onReset={handleReset}
            onApply={handleApply}
            onClose={() => setIsFilterOpen(false)}
          />
        </div>
      )}

      {/* RIGHT: Reports + range selector */}
      <div className="tw-flex-1 tw-flex tw-flex-col tw-p-4 tw-gap-4">
        <ReportHeader activeTab={activeTab} onChangeTab={setActiveTab} />
        <div
          className="tw-text-xs tw-text-slate-700 tw-cursor-pointer hover:tw-underline"
          onClick={() => setIsFilterOpen((prev) => !prev)}
          title="Toggle Filters"
        >
          {buildViewingSummary(activeTab, filters)}
        </div>

        <div className="tw-flex-1 tw-bg-white tw-shadow-sm tw-rounded-lg tw-p-4 tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex-1 tw-flex tw-flex-col">
            {activeTab === TABS.PRODUCTION && (
              <ProductionReport filters={filters} range={range} />
            )}
            {activeTab === TABS.EFFICIENCY && (
              <EfficiencyReport filters={filters} range={range} />
            )}
            {activeTab === TABS.RAW && (
              <RawOnOffReport
                filters={filters}
                range={range}
                fetchRawData={fetchRawData}
              />
            )}
          </div>
          <RangeSelector
            value={range}
            onChange={setRange}
            intervalSeconds={filters.intervalSeconds}
            durationSeconds={filters.durationSeconds}
            label={getWindowLabel(filters, range)}
          />
        </div>
      </div>
    </div>
  );
}
